import React, { useState, useEffect, useCallback } from 'react';
import { getAgenturLeads } from '../../api';
import CustomerDetail from '../CustomerDetail';
import DashboardNav from '../../components/dashboard/DashboardNav';
import OrgStylePagination from '../../components/common/OrgStylePagination';
import { useNavigate, useParams } from 'react-router-dom';

function AgenturLeads({ user, agenturId: propAgenturId, a_team_id: propATeamId, onBack }) {
  const [leads, setLeads] = useState([]);
  const [leadPage, setLeadPage] = useState(1);
  const [leadPerPage, setLeadPerPage] = useState(25);
  const [leadTotal, setLeadTotal] = useState(0);
  const [leadTotalPages, setLeadTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [availableTeams, setAvailableTeams] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [availableServiceProviders, setAvailableServiceProviders] = useState([]);
  const [activeSection] = useState('organization-leads');
  const navigate = useNavigate();
  const { agenturId: urlAgenturId, customerId, a_team_id: urlATeamId } = useParams();

  // Agentur-ID und Team-ID bestimmen: Props > URL > User-Objekt
  const agenturId = propAgenturId || urlAgenturId || user?.agentur_id;
  const a_team_id = propATeamId || urlATeamId;

  const [filters, setFilters] = useState({
    teamId: '',
    sortBy: 'erstellt',
    sortOrder: 'desc',
    dateFrom: '',
    dateTo: '',
    statusPv: '',
    statusWp: '',
    projekt: '',
    search: '', // Umbenennung zu 'search' für Konsistenz mit CallcenterLeads
    dienstleister: '', // Neuer Dienstleister-Filter
    sortByQualiDatum: false, // Neuer Filter für Sortierung nach Quali-Datum
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Verwende organizationId aus Props
  const currentOrganizationId = agenturId;

  // Responsive Styles (wie in Leads.js)
  const styles = {
    container: {
      margin: '0 auto',
      maxWidth: '1400px',
      padding: isMobile ? '5px' : '15px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: '15px',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '10px' : '0',
    },
    title: {
      color: '#2d3748',
      fontSize: isMobile ? '1.2rem' : '1.6rem',
      margin: 0,
    },
    filters: {
      background: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      padding: isMobile ? '10px' : '15px',
      marginBottom: '15px',
    },
    filterRow: {
      display: 'flex',
      gap: isMobile ? '8px' : '15px',
      marginBottom: '12px',
      flexWrap: 'wrap',
      flexDirection: isMobile ? 'column' : 'row',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: isMobile ? 'auto' : '120px',
      flex: isMobile ? '1' : 'none',
    },
    filterLabel: {
      fontSize: '0.8rem',
      fontWeight: '500',
      color: '#4a5568',
      marginBottom: '4px',
    },
    filterInput: {
      padding: '6px 8px',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.8rem',
    },
    resetButton: {
      padding: '6px 12px',
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '0.8rem',
      cursor: 'pointer',
      marginTop: isMobile ? '8px' : '15px',
      width: isMobile ? '100%' : 'auto',
    },
    tableContainer: {
      background: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      maxHeight: '70vh',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? '900px' : isTablet ? '1000px' : 'auto',
      fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.85rem',
    },
    th: {
      background: '#f7fafc',
      padding: isMobile ? '6px 4px' : isTablet ? '8px 6px' : '10px 8px',
      textAlign: 'left',
      fontWeight: '600',
      color: '#4a5568',
      borderBottom: '1px solid #e2e8f0',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem',
      whiteSpace: 'nowrap',
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    td: {
      padding: isMobile ? '6px 4px' : isTablet ? '8px 6px' : '10px 8px',
      borderBottom: '1px solid #e2e8f0',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem',
      maxWidth: isMobile ? '80px' : isTablet ? '100px' : '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    dateColumn: {
      minWidth: isMobile ? '110px' : '120px',
      maxWidth: isMobile ? '130px' : '150px',
    },
    productColumn: {
      minWidth: isMobile ? '50px' : '60px',
      maxWidth: isMobile ? '50px' : '60px',
    },
    nameColumn: {
      minWidth: isMobile ? '80px' : '100px',
      maxWidth: isMobile ? '120px' : '150px',
    },
    emailColumn: {
      minWidth: isMobile ? '90px' : '120px',
      maxWidth: isMobile ? '120px' : '180px',
    },
    phoneColumn: {
      minWidth: isMobile ? '80px' : '100px',
      maxWidth: isMobile ? '80px' : '100px',
    },
    teamColumn: {
      minWidth: isMobile ? '60px' : '80px',
      maxWidth: isMobile ? '60px' : '100px',
    },
    statusColumn: {
      minWidth: isMobile ? '60px' : '80px',
      maxWidth: isMobile ? '80px' : '100px',
    },
    row: {
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    statusBadge: {
      display: 'inline-block',
      padding: isMobile ? '0.15rem 0.3rem' : '0.2rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: isMobile ? '0.55rem' : '0.65rem',
      fontWeight: '500',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      minWidth: isMobile ? '35px' : '50px',
      maxWidth: isMobile ? '60px' : '80px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    statusNew: {
      backgroundColor: '#ebf8ff',
      color: '#3182ce',
    },
    statusCompleted: {
      backgroundColor: '#c6f6d5',
      color: '#38a169',
    },
    statusCancelled: {
      backgroundColor: '#fed7d7',
      color: '#e53e3e',
    },
    productBadges: {
      display: 'flex',
      gap: '0.15rem',
      flexWrap: 'wrap',
    },
    productBadge: {
      display: 'inline-block',
      padding: isMobile ? '0.1rem 0.25rem' : '0.15rem 0.4rem',
      borderRadius: '0.2rem',
      fontSize: isMobile ? '0.55rem' : '0.6rem',
      fontWeight: '700',
    },
    pvBadge: {
      backgroundColor: '#c3dafe',
      color: '#4a6de5',
    },
    wpBadge: {
      backgroundColor: '#b2f5ea',
      color: '#3182ce',
    },
    loading: {
      textAlign: 'center',
      padding: '20px',
      color: '#718096',
      fontSize: '1rem',
    },
    error: {
      color: '#e53e3e',
      backgroundColor: '#fed7d7',
      padding: '8px',
      borderRadius: '4px',
      marginBottom: '12px',
      textAlign: 'center',
      fontSize: '0.85rem',
    },
    noData: {
      textAlign: 'center',
      padding: '20px',
      color: '#a0aec0',
      fontSize: '0.9rem',
    },
  };

  const fetchLeads = useCallback(async () => {
    if (!user?.id || !agenturId) {
      setError('Benutzer oder Agentur-ID fehlt');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const hasDateRange = Boolean(filters.dateFrom && filters.dateTo);
      const data = await getAgenturLeads(user.id, agenturId, a_team_id, {
        page: leadPage,
        perPage: leadPerPage,
        startDate: hasDateRange ? filters.dateFrom : null,
        endDate: hasDateRange ? filters.dateTo : null,
        search: filters.search || null,
        statusPv: filters.statusPv || null,
        statusWp: filters.statusWp || null,
        projekt: filters.projekt || null,
        dienstleister: filters.dienstleister || null,
        teamName: filters.teamId || null,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        sortByQualiDatum: filters.sortByQualiDatum,
        includeFilterMeta: leadPage === 1,
      });
      if (data.success) {
        setLeads(data.leads || []);
        const t = typeof data.total === 'number' ? data.total : 0;
        const tp = typeof data.totalPages === 'number' ? data.totalPages : 0;
        setLeadTotal(t);
        setLeadTotalPages(tp);
        if (tp > 0 && leadPage > tp) {
          setLeadPage(tp);
        }
        if (data.filterMeta) {
          setAvailableTeams(data.filterMeta.teams || []);
          setAvailableProjects(data.filterMeta.projects || []);
          setAvailableServiceProviders(data.filterMeta.dienstleister || []);
        }
      } else {
        setError(data.error || 'Fehler beim Abrufen der Leads');
        setLeads([]);
        setLeadTotal(0);
        setLeadTotalPages(0);
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [
    user?.id, agenturId, a_team_id, leadPage, leadPerPage,
    filters.teamId, filters.sortBy, filters.sortOrder, filters.dateFrom, filters.dateTo,
    filters.statusPv, filters.statusWp, filters.projekt, filters.search, filters.dienstleister,
    filters.sortByQualiDatum,
  ]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Status-Farben-Funktion
  const getStatusColor = (status) => {
    if (!status) return "";
    
    status = status.toLowerCase();
    if (status.includes('offen') || status.includes('neu')) return "status-new";
    if (status.includes('bearbeitung') || status.includes('in arbeit')) return "status-in-progress";
    if (status.includes('abgeschlossen') || status.includes('fertig')) return "status-completed";
    if (status.includes('qualifiziert') || status.includes('bestätigt')) return "status-completed";
    if (status.includes('abgelehnt') || status.includes('storniert') || status.includes('kein interesse') || status.includes('widerruf') || status.includes('ungültig') || status.includes('test')) return "status-cancelled";
    return "";
  };

  // Status-Rendering mit Validierung und Farben
  const renderStatus = (status, productType, leadProduct) => {
    const hasProduct = leadProduct && leadProduct.toLowerCase().includes(productType.toLowerCase());
    
    if (!hasProduct) {
      return <span>-</span>;
    }
    
    if (!status || status.trim() === '') {
      return <span className="status-badge status-new">Offen</span>;
    }
    
    return (
      <span className={`status-badge ${getStatusColor(status)}`}>
        {status}
      </span>
    );
  };

  // Filter-Handler
  const handleFilterChange = (filterName, value) => {
    setLeadPage(1);
    setFilters((prev) => {
      const newFilters = { ...prev, [filterName]: value };
      if (filterName === 'teamId') {
        newFilters.dienstleister = '';
      }
      return newFilters;
    });
  };

  // Filter zurücksetzen
  const resetFilters = () => {
    setLeadPage(1);
    setFilters({
      projekt: '',
      teamId: '',
      sortBy: 'erstellt',
      sortOrder: 'desc',
      dateFrom: '',
      dateTo: '',
      statusPv: '',
      statusWp: '',
      search: '',
      dienstleister: '',
      sortByQualiDatum: false,
    });
  };

  // Suchfeld leeren
  const clearNameSearch = () => {
    setLeadPage(1);
    setFilters((prev) => ({ ...prev, search: '' }));
  };

  // Kunde auswählen
  const handleCustomerSelect = (id) => {
    if (a_team_id) {
      // Wenn wir in einer Team-Ansicht sind, verwende die Team-URL
      navigate(`/agenturen/${agenturId}/teams/${a_team_id}/leads/${id}`);
    } else if (agenturId) {
      // Für Agenturleiter: richtige Detailroute
      navigate(`/agenturen/${agenturId}/leads/${id}`);
    } else if (currentOrganizationId) {
      navigate(`/organization-leads/${currentOrganizationId}/${id}`);
    } else {
      navigate(`/organization-leads/${id}`);
    }
  };

  // Zurück zur Liste
  const handleBackToList = () => {
    if (a_team_id) {
      // Wenn wir in einer Team-Ansicht sind, zurück zur Team-Leads-Liste
      navigate(`/agenturen/${agenturId}/teams/${a_team_id}/leads`);
    } else if (currentOrganizationId) {
      navigate(`/organization-leads/${currentOrganizationId}`);
    } else {
      navigate('/organization-leads');
    }
  };

  // Handler für Section-Wechsel (wie in Leads.js)
  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      window.location.href = '/dashboard';
    } else if (section === 'leads') {
      window.location.href = '/leads-normal';
    } else if (section === 'stellungnahmen') {
      window.location.href = '/stellungnahmen';
    } else if (section === 'termine') {
      window.location.href = '/termine';
    } else if (section === 'organization-leads') {
      // Bleibe auf dieser Seite
    }
  };

  // Detailansicht
  if (customerId) {
    return (
      <CustomerDetail 
        user={user} 
        customerId={customerId} 
        onBack={handleBackToList} 
      />
    );
  }

  // Listenansicht mit Navigation
  return (
    <>
      <DashboardNav
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        user={user}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={true}
      />

      <div style={styles.container}>

        {/* Header */}
        <div className="page-header-wrapper">
          <button className="back-button" onClick={() => navigate("/agenturen")}>
            ← Zurück
          </button>
          <h2>Agentur-Leads
            <span style={{ fontSize: '0.7em', color: '#4a6de5', marginLeft: '8px', fontWeight: 'normal' }}>
                ({leads.length})
            </span>
          </h2>
        </div>

        {/* Filter-Bereich - ERWEITERT mit Namenssuche */}
        <div className="leads-filters">
          {/* NEUE SUCHZEILE */}
          <div className="search-row">
            <div className="search-group search-group-flex">
              <label style={{ marginRight: 12 }}>🔍 Namenssuche:</label>
              <div className="search-input-flex">
                <input
                  type="text"
                  placeholder="Name eingeben..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="search-input"
                />
                {filters.search && (
                  <button 
                    className="clear-search-button"
                    onClick={clearNameSearch}
                    title="Suche löschen"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            
            {filters.search && (
              <div className="search-results-info">
                {leads.length === 0 ? (
                  <span className="no-results">Keine Ergebnisse für "{filters.search}"</span>
                ) : (
                  <span className="results-count">
                    {leads.length} Ergebnis{leads.length !== 1 ? 'se' : ''} für "{filters.search}"
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label>Projekt:</label>
              <select 
                value={filters.projekt}
                onChange={e => handleFilterChange('projekt', e.target.value)}
              >
                <option value="">Alle Projekte</option>
                {availableProjects.map(project => (
                  <option key={project} value={project}>{project}</option>
                ))}
              </select>
            </div>
            {/* Team-Dropdown nur anzeigen, wenn nicht Agenturleiter und kein a_team_id */}
            {!a_team_id && user.role_type !== 7 && (
              <div className="filter-group">
                <label>Projekt:</label>
                <select 
                  value={filters.teamId}
                  onChange={e => handleFilterChange('teamId', e.target.value)}
                >
                  <option value="">Alle Teams</option>
                  {availableTeams.map(team => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="filter-group">
              <label>Sortieren nach:</label>
              <select 
                value={filters.sortBy}
                onChange={e => handleFilterChange('sortBy', e.target.value)}
                className="sort-select"
              >
                <option value="erstellt">Erstellt</option>
                <option value="name">Name</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>Reihenfolge:</label>
              <select 
                value={filters.sortOrder}
                onChange={e => handleFilterChange('sortOrder', e.target.value)}
              >
                <option value="desc">Absteigend</option>
                <option value="asc">Aufsteigend</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Dienstleister:</label>
              <select 
                value={filters.dienstleister}
                onChange={e => handleFilterChange('dienstleister', e.target.value)}
              >
                <option value="">Alle Dienstleister</option>
                {availableServiceProviders.map(provider => (
                  <option key={provider} value={provider}>
                    {provider}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="filter-row">
            <div className="filter-group">
              <label>Von Datum:</label>
              <input 
                type="date"
                value={filters.dateFrom}
                onChange={e => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            
            <div className="filter-group">
              <label>Bis Datum:</label>
              <input 
                type="date"
                value={filters.dateTo}
                onChange={e => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>PV Status:</label>
              <select 
                value={filters.statusPv}
                onChange={e => handleFilterChange('statusPv', e.target.value)}
              >
                <option value="">Alle Status</option>
                <option value="Offen">Offen</option>
                <option value="Qualifiziert">Qualifiziert</option>
                <option value="Kein Interesse mehr">Kein Interesse mehr</option>
                <option value="Widerruf">Widerruf</option>
                <option value="Telefonnummer ungültig">Telefonnummer ungültig</option>
                <option value="Test Lead">Test Lead</option>
                <option value="Kwh zu niedrig">Kwh zu niedrig</option>
                <option value="Denkmalschutz">Denkmalschutz</option>
                <option value="Dach muss saniert werden">Dach muss saniert werden</option>
                <option value="Technisch nicht möglich">Technisch nicht möglich</option>
                <option value="Dublette">Dublette</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>WP Status:</label>
              <select 
                value={filters.statusWp}
                onChange={e => handleFilterChange('statusWp', e.target.value)}
              >
                <option value="">Alle Status</option>
                <option value="Offen">Offen</option>
                <option value="Qualifiziert">Qualifiziert</option>
                <option value="Kein Interesse mehr">Kein Interesse mehr</option>
                <option value="Widerruf">Widerruf</option>
                <option value="Telefonnummer ungültig">Telefonnummer ungültig</option>
                <option value="Test Lead">Test Lead</option>
                <option value="Kwh zu niedrig">Kwh zu niedrig</option>
                <option value="Denkmalschutz">Denkmalschutz</option>
                <option value="Dach muss saniert werden">Dach muss saniert werden</option>
                <option value="Technisch nicht möglich">Technisch nicht möglich</option>
                <option value="Dublette">Dublette</option>
              </select>
            </div>
            
            <div className="filter-group">
              <button className="reset-filters-button" onClick={resetFilters}>
                Alle Filter zurücksetzen
              </button>
            </div>
          </div>
          
        </div>
        {error && <div style={styles.error}>{error}</div>}
        {loading ? (
          <div className="loading">Leads werden geladen...</div>
        ) : leads.length === 0 ? (
          <div className="no-data">
            {filters.search ? (
              <div>
                <p>Keine Leads mit dem Namen "<strong>{filters.search}</strong>" gefunden.</p>
                <button onClick={clearNameSearch} className="clear-search-suggestion">
                  Suche löschen und alle {a_team_id ? 'Team-Leads' : 'Agentur-Leads'} anzeigen
                </button>
              </div>
            ) : (
              a_team_id ? 'Keine Leads für dieses Team gefunden.' : 'Keine Leads gefunden.'
            )}
          </div>
        ) : (
          <>
            <OrgStylePagination
              currentPage={leadPage}
              totalPages={leadTotalPages}
              totalCount={leadTotal}
              perPage={leadPerPage}
              onPageChange={(p) => setLeadPage(p)}
              onPerPageChange={(n) => {
                setLeadPage(1);
                setLeadPerPage(n);
              }}
              countSingular="Lead"
              countPlural="Leads"
              perPageLabel="Leads pro Seite:"
            />
            <div className="table-container">
            <table className="leads-table">
              <thead>
                <tr>
                  <th>Erstellt</th>
                  <th>Produkt</th>
                  <th>Opt In</th>
                  <th>Dienstleister</th>
                  <th>Name</th>
                  <th>E-Mail</th>
                  <th>Handy</th>
                  <th>Projekt</th>
                  <th>PV Status</th>
                  <th>WP Status</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr 
                    key={lead.id}
                    style={{ cursor: 'pointer' }}
                    className={`${filters.search && `${lead.anrede || ''} ${lead.name || ''} ${lead.nachname || ''}`.toLowerCase().includes(filters.search.toLowerCase()) ? 'search-highlight' : ''}`}
                    onClick={() => handleCustomerSelect(lead.id)}
                  >
                    <td>
                      {new Date(lead.erstellt).toLocaleString('de-DE', {
                        dateStyle: 'short',
                        timeStyle: 'short'
                      })}
                    </td>
                    <td>
                      {lead.produkt ? (
                        <div className="product-badges">
                          {lead.produkt.toLowerCase().includes('pv') && (
                            <span className="product-badge pv-badge">PV</span>
                          )}
                          {lead.produkt.toLowerCase().includes('wp') && (
                            <span className="product-badge wp-badge">WP</span>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{lead.opt_in ? 'Ja' : 'Nein'}</td>
                    <td>{lead.dienstleister || '-'}</td>
                    <td>
                      <span className={filters.search && `${lead.anrede || ''} ${lead.name || ''} ${lead.nachname || ''}`.toLowerCase().includes(filters.search.toLowerCase()) ? 'name-highlight' : ''}>
                        {`${lead.anrede ? lead.anrede + ' ' : ''}${lead.name || ''} ${lead.nachname || ''}`.trim() || '-'}
                      </span>
                    </td>
                    <td>{lead.email || '-'}</td>
                    <td>{lead.handy || '-'}</td>
                    <td>{lead.projekt || '-'}</td>
                    <td>{renderStatus(lead.status_pv, 'pv', lead.produkt)}</td>
                    <td>{renderStatus(lead.status_wp, 'wp', lead.produkt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
        
        {/* Erweiterte Styles von CallcenterLeads */}
        <style jsx>{`
          .page-header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            padding: 20px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .page-header-wrapper h2 {
            margin: 0;
            color: #2d3748;
            font-size: 1.5rem;
          }

          .back-button {
            padding: 8px 16px;
            background: #4a6de5;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 0.875rem;
            transition: background-color 0.2s;
          }

          .back-button:hover {
            background: #3c5ce0;
          }

          .leads-filters {
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            padding: 20px;
            margin-bottom: 20px;
          }

          /* NEUE STYLES FÜR SUCHE */
          .search-row {
            display: flex;
            align-items: center;
            gap: 20px;
            margin-bottom: 16px;
            padding: 16px;
            background: #f8fafc;
            border-radius: 8px;
            border: 1px solid #e2e8f0;
          }

          .search-group-flex {
            display: flex;
            flex-direction: row;
            align-items: center;
            min-width: 0;
            flex: 1;
          }

          .search-input-flex {
            display: flex;
            flex-direction: row;
            align-items: center;
            flex: 1;
            min-width: 0;
            gap: 8px;
            max-width: 400px;
            width: 100%;
          }

          .search-input {
            flex: 1 1 0;
            min-width: 0;
            width: auto;
            padding: 10px 12px;
            border: 2px solid #e2e8f0;
            border-radius: 6px;
            font-size: 0.95rem;
            transition: border-color 0.2s, box-shadow 0.2s;
          }

          .search-input:focus {
            outline: none;
            border-color: #4a6de5;
            box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
          }

          .clear-search-button {
            background: none;
            border: none;
            color: #a0aec0;
            cursor: pointer;
            font-size: 1.1rem;
            width: 32px;
            height: 32px;
            min-width: 32px;
            min-height: 32px;
            max-width: 32px;
            max-height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background-color 0.2s, color 0.2s;
            margin-left: 0;
          }

          .clear-search-button:hover {
            background-color: #f7fafc;
            color: #e53e3e;
          }

          .search-results-info {
            display: flex;
            align-items: center;
            font-size: 0.875rem;
          }

          .no-results {
            color: #e53e3e;
            font-weight: 500;
          }

          .results-count {
            color: #38a169;
            font-weight: 500;
          }

          .clear-search-suggestion {
            background: #4a6de5;
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 0.875rem;
            margin-top: 8px;
            transition: background-color 0.2s;
          }

          .clear-search-suggestion:hover {
            background: #3c5ce0;
          }

          /* HIGHLIGHT STYLES */
          .search-highlight {
            background-color: #fff5f5 !important;
          }

          .search-highlight:hover {
            background-color: #fed7d7 !important;
          }

          .name-highlight {
            background-color: #fef3c7;
            padding: 2px 4px;
            border-radius: 3px;
            font-weight: 600;
          }

          /* FILTER STYLES */
          .filter-row {
            display: flex;
            gap: 20px;
            margin-bottom: 16px;
            flex-wrap: wrap;
          }

          .filter-group {
            display: flex;
            flex-direction: column;
            min-width: 150px;
            flex: 1;
          }

          .filter-group label {
            font-size: 0.875rem;
            font-weight: 500;
            color: #4a5568;
            margin-bottom: 6px;
          }

          .filter-group select,
          .filter-group input {
            padding: 8px 12px;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            font-size: 0.875rem;
            transition: border-color 0.2s;
          }

          .filter-group select:focus,
          .filter-group input:focus {
            outline: none;
            border-color: #4a6de5;
            box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
          }

          .reset-filters-button {
            padding: 8px 16px;
            background-color: #f3f4f6;
            color: #4b5563;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            cursor: pointer;
            font-size: 0.875rem;
            margin-top: 22px;
            transition: all 0.2s;
          }

          .reset-filters-button:hover {
            background-color: #e5e7eb;
            color: #1f2937;
          }

          /* CHECKBOX FILTER STYLES */
          .checkbox-filter {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            margin-top: 22px;
            padding: 8px 12px;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            background-color: #f8fafc;
            transition: all 0.2s;
            user-select: none;
          }

          .checkbox-filter:hover {
            background-color: #edf2f7;
            border-color: #cbd5e0;
          }

          .checkbox-input {
            width: 16px;
            height: 16px;
            margin: 0;
            cursor: pointer;
            accent-color: #4a6de5;
          }

          .checkbox-label {
            font-size: 0.875rem;
            font-weight: 500;
            color: #4a5568;
            cursor: pointer;
          }

          .checkbox-filter:has(.checkbox-input:checked) {
            background-color: #ebf4ff;
            border-color: #4a6de5;
          }

          .checkbox-filter:has(.checkbox-input:checked) .checkbox-label {
            color: #4a6de5;
            font-weight: 600;
          }

          /* TABLE STYLES */
          .leads-table {
            width: 100%;
            border-collapse: collapse;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            table-layout: fixed;
          }

          /* Container für die Tabelle mit Scrollbalken */
          .table-container {
            position: relative;
            width: 100%;
            overflow-x: auto;
            overflow-y: auto;
            max-height: calc(100vh - 300px);
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          /* Scrollbalken-Styling */
          .table-container::-webkit-scrollbar {
            height: 12px;
            width: 12px;
          }

          .table-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 6px;
          }

          .table-container::-webkit-scrollbar-thumb {
            background: #cbd5e0;
            border-radius: 6px;
          }

          .table-container::-webkit-scrollbar-thumb:hover {
            background: #a0aec0;
          }

          /* Header für bessere Lesbarkeit beim Scrollen */
          .leads-table thead {
            position: sticky;
            top: 0;
            z-index: 1;
          }

          .leads-table thead th {
            position: sticky;
            top: 0;
            background: #f7fafc;
            z-index: 2;
          }

          .leads-table th {
            background: #f7fafc;
            padding: 8px 12px;
            text-align: left;
            font-weight: 600;
            color: #4a5568;
            border-bottom: 1px solid #e2e8f0;
            font-size: 0.75rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .leads-table td {
            padding: 8px 12px;
            border-bottom: 1px solid #e2e8f0;
            vertical-align: middle;
            font-size: 0.75rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          /* PRODUCT BADGES */
          .product-badges {
            display: flex;
            gap: 4px;
          }

          .product-badge {
            display: inline-block;
            padding: 0.2rem 0.5rem;
            border-radius: 0.25rem;
            font-size: 0.7rem;
            font-weight: 700;
            text-align: center;
          }

          .pv-badge {
            background-color: #c3dafe;
            color: #4a6de5;
          }

          .wp-badge {
            background-color: #b2f5ea;
            color: #3182ce;
          }

          /* STATUS BADGES */
          .status-badge {
            display: inline-block;
            padding: 0.25rem 0.5rem;
            border-radius: 0.375rem;
            font-size: 0.75rem;
            font-weight: 500;
            text-align: center;
            white-space: nowrap;
          }

          .status-new {
            background-color: #ebf8ff;
            color: #3182ce;
          }

          .status-in-progress {
            background-color: #fefcbf;
            color: #d69e2e;
          }

          .status-completed {
            background-color: #c6f6d5;
            color: #38a169;
          }

          .status-cancelled {
            background-color: #fed7d7;
            color: #e53e3e;
          }

          /* UTILITY STYLES */
          .loading {
            text-align: center;
            padding: 40px;
            color: #718096;
            font-size: 1rem;
          }

          .no-data {
            text-align: center;
            padding: 40px;
            color: #a0aec0;
            font-size: 1rem;
          }

          /* RESPONSIVE DESIGN */
          @media (max-width: 768px) {
            .search-row {
              flex-direction: column;
              gap: 12px;
            }

            .search-group-flex {
              min-width: auto;
            }

            .filter-row {
              flex-direction: column;
              gap: 12px;
            }

            .filter-group {
              min-width: auto;
            }

            .leads-table {
              font-size: 0.8rem;
            }

            .leads-table th,
            .leads-table td {
              padding: 8px 6px;
            }
          }

          /* ACCESSIBILITY */
          .search-input:focus,
          .filter-group select:focus,
          .filter-group input:focus {
            outline: none;
            border-color: #4a6de5;
            box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
          }

          .reset-filters-button:focus,
          .clear-search-button:focus,
          .clear-search-suggestion:focus {
            outline: 2px solid #4a6de5;
            outline-offset: 2px;
          }
        `}</style>
      </div>
    </>
  );
}

export default AgenturLeads;